/**
 * WAM (Web Audio Module) v2 bootstrap layer.
 *
 * WAMs are browser-native audio plugins that ship as ES modules with
 * an AudioWorklet-backed DSP node + a Custom-Element UI. They match
 * our existing plugin contract almost 1:1 — the challenge is that
 * `WebAudioModule.createInstance(hostGroupId, ctx)` is async (needs
 * `AudioContext.audioWorklet.addModule(url)` + dynamic `import(url)`),
 * while `Bus.buildSlot` instantiates plugins synchronously on the
 * hot path.
 *
 * We reconcile the two by front-loading every WAM class during engine
 * boot via `preloadWam`, caching the loaded constructor on a
 * module-level `Map<url, WamClass>`. Once the preload promise resolves,
 * `Bus.buildSlot` can call `new WamClass(ctx, hostGroupId)` synchronously
 * because the async cost has already been paid.
 *
 * Host-group id: every WAM instance needs an id that scopes its host's
 * transport + parameter-observer state. Instances sharing an id see
 * each other's automation events; instances with different ids don't.
 * We mint one id per `AudioContext` (lazy, process-level) so all our
 * WAMs share the same host. Offline render contexts re-init via
 * `initWamHostGroup(offlineCtx)` and get their own id.
 *
 * Error policy: if a WAM fails to load (404, CORS, module parse error),
 * `preloadWam` logs + resolves to `null` rather than throwing. The
 * engine keeps booting; the WAM simply doesn't appear in the registry.
 * This keeps the whole app running if a single WAM CDN goes down or
 * the user's network blocks a particular asset.
 */

// ── Minimal WAM v2 type surface ────────────────────────────────────
//
// The @webaudiomodules/api npm package ships richer types, but pulling
// in a new dep just for type declarations is overkill while we only
// consume a tiny slice of the SDK. These shapes match the WAM v2
// specification at https://www.w3.org/community/webaudioplugins/ —
// add properties here as new adapters need them.

export interface WamParameterInfo {
  id: string
  label?: string
  type: 'float' | 'int' | 'boolean' | 'choice'
  defaultValue: number
  minValue: number
  maxValue: number
  discreteStep?: number
  exponent?: number
  choices?: readonly string[]
  units?: string
}

export type WamParameterInfoMap = Record<string, WamParameterInfo>

export interface WamAutomationEvent {
  type: 'wam-automation'
  time?: number
  data: { id: string; value: number; normalized?: boolean }
}

export interface WamMidiEvent {
  type: 'wam-midi'
  time?: number
  data: { bytes: readonly number[] | Uint8Array }
}

export type WamEvent = WamAutomationEvent | WamMidiEvent

export interface WamNode extends AudioNode {
  scheduleEvents(...events: readonly WamEvent[]): void
  getParameterInfo?(): Promise<WamParameterInfoMap> | WamParameterInfoMap
}

export interface WamInstance {
  audioNode: WamNode
  createGui(): Promise<HTMLElement> | HTMLElement
  destroyGui?(el: HTMLElement): void
  destroy(): void
  getState?(): Promise<unknown> | unknown
  setState?(state: unknown): Promise<void> | void
}

export interface WamClass {
  createInstance(
    hostGroupId: string,
    ctx: BaseAudioContext,
    initialState?: unknown,
  ): Promise<WamInstance>
}

// ── Host group ─────────────────────────────────────────────────────

const hostGroupByCtx = new WeakMap<BaseAudioContext, string>()

/**
 * Mint (or return) a stable host-group id for this AudioContext. Safe
 * to call repeatedly on the same context — subsequent calls return the
 * same id. Offline contexts get their own id, which is correct: a
 * fresh render shouldn't share transport state with the live engine.
 */
export function initWamHostGroup(ctx: BaseAudioContext): string {
  const existing = hostGroupByCtx.get(ctx)
  if (existing) return existing
  // crypto.randomUUID is widely supported in browser + Node; the UUID
  // is purely an opaque scope key so any stable random string works.
  const id = `busswerks-${crypto.randomUUID()}`
  hostGroupByCtx.set(ctx, id)
  return id
}

/**
 * Read the host-group id for a context that's already been
 * initialised. Adapters call this lazily at `createInstance` time.
 * Throws if init was skipped — callers should always run
 * `initWamHostGroup` on engine boot before any WAM instantiation.
 */
export function getWamHostGroupId(ctx: BaseAudioContext): string {
  const id = hostGroupByCtx.get(ctx)
  if (!id) {
    throw new Error(
      '[wam] host group not initialised for this AudioContext — call initWamHostGroup(ctx) during engine boot',
    )
  }
  return id
}

// ── Preload cache ──────────────────────────────────────────────────

/**
 * Module-level cache of loaded WAM constructors, keyed by source URL.
 * Reused across hot-reloads so we don't pay the import + addModule
 * cost more than once per session.
 */
const wamClassByUrl = new Map<string, WamClass>()

/**
 * Inflight-promise cache — if two registrations request the same URL
 * concurrently (e.g. during parallel `registerWams` boot), we dedupe
 * to one network fetch. Keys are URL strings; values resolve to the
 * same `WamClass | null` as `preloadWam` itself returns.
 */
const inflightByUrl = new Map<string, Promise<WamClass | null>>()

/**
 * Shape the ES-module export can take. Most WAMs default-export the
 * class; some older ones export it as `WebAudioModule` or a named
 * export.
 */
interface WamModuleExports {
  default?: WamClass
  WebAudioModule?: WamClass
}

function pickWamClass(mod: WamModuleExports): WamClass | null {
  if (typeof mod.default?.createInstance === 'function') return mod.default
  if (typeof mod.WebAudioModule?.createInstance === 'function') {
    return mod.WebAudioModule
  }
  return null
}

/**
 * Dynamically import a WAM's entry module + register its
 * AudioWorkletProcessor with the target context. Returns the WAM
 * class factory so callers can synchronously `createInstance` later.
 *
 * Failure modes — all non-fatal (returns null, logs to console):
 *   - URL 404 / network error
 *   - CORS block on the module or its processor assets
 *   - Module doesn't export a class with `createInstance`
 *
 * The WAM v2 spec requires the module to call
 * `AudioWorkletGlobalScope.registerProcessor` from inside its
 * AudioWorklet module (the WAM itself handles `addModule` internally
 * via its constructor). We don't need to call `ctx.audioWorklet.addModule`
 * here — the WAM's `createInstance` does it on first construction.
 */
export async function preloadWam(
  ctx: BaseAudioContext,
  url: string,
): Promise<WamClass | null> {
  // Cache hit — already loaded in a prior boot this session.
  const cached = wamClassByUrl.get(url)
  if (cached) return cached

  // Inflight hit — another call is already loading this URL, reuse
  // its promise so we don't duplicate the network request.
  const inflight = inflightByUrl.get(url)
  if (inflight) return inflight

  const loadPromise = loadWamClass(ctx, url)
  inflightByUrl.set(url, loadPromise)
  const cls = await loadPromise
  inflightByUrl.delete(url)
  if (cls) wamClassByUrl.set(url, cls)
  return cls
}

async function loadWamClass(
  _ctx: BaseAudioContext,
  url: string,
): Promise<WamClass | null> {
  try {
    const mod = (await import(/* @vite-ignore */ url)) as WamModuleExports
    const cls = pickWamClass(mod)
    if (!cls) {
      console.warn(
        `[wam] module at ${url} does not export a class with createInstance()`,
      )
      return null
    }
    return cls
  } catch (err) {
    console.warn(`[wam] failed to load ${url}:`, err)
    return null
  }
}

/**
 * Synchronously look up a previously-preloaded WAM class. Adapter
 * factories use this at `createNode` time — the WAM must already be
 * in cache or the sync contract breaks. Returns null if the WAM
 * failed to preload (caller should render a placeholder).
 */
export function getPreloadedWamClass(url: string): WamClass | null {
  return wamClassByUrl.get(url) ?? null
}

/** Test-only: reset all caches. Not exported from the package barrel. */
export function __resetWamCachesForTests(): void {
  wamClassByUrl.clear()
  inflightByUrl.clear()
}
